import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Palette,
  Droplets,
  Save,
  History,
  Settings,
  X,
  Beaker,
  FlaskConical,
  Scale,
  Timer,
  User,
  FileText,
  ChevronRight,
  Check,
  RotateCcw,
  Plus,
  Minus,
  Percent,
  Package,
  Waves,
  Edit2,
  Trash2
} from "lucide-react";

// ============================================================
// CONFIGURAZIONE
// ============================================================
const OSSIGENI = ["6 vol", "10 vol", "20 vol", "30 vol", "40 vol"];
const GRAMMATURE_BASE = ["30g", "35g", "40g", "45g", "50g", "55g", "60g", "70g", "80g"];
const GRAMMATURE_TONALIZZANTE = ["20g", "25g", "30g", "35g", "40g", "45g", "50g"];
const PERCENTUALI = [5, 10, 20, 30, 50, 70, 90, 100];

// ============================================================
// COMPONENTE SELEZIONE COLORE
// ============================================================
function ColorSelector({ 
  index, 
  colore, 
  onChange, 
  onRemove, 
  prodottiDisponibili,
  marcaSelezionata,
  disabled = false
}) {
  const prodottiFiltrati = marcaSelezionata 
    ? prodottiDisponibili.find(m => m.marca === marcaSelezionata)?.prodotti || []
    : [];

  return (
    <div className={`relative p-3 rounded-xl border-2 transition-all ${
      colore.attivo 
        ? 'bg-white border-amber-300 shadow-sm' 
        : 'bg-slate-50 border-dashed border-slate-200'
    }`}>
      {/* Header con toggle e percentuale */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange({ ...colore, attivo: !colore.attivo })}
            disabled={disabled}
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              colore.attivo 
                ? 'bg-amber-500 text-white' 
                : 'bg-slate-200 text-slate-400'
            }`}
          >
            {index + 1}
          </button>
          <span className={`text-sm font-medium ${colore.attivo ? 'text-slate-700' : 'text-slate-400'}`}>
            Colore {index + 1}
          </span>
        </div>
        
        {colore.attivo && (
          <Select 
            value={String(colore.percentuale)} 
            onValueChange={(v) => onChange({ ...colore, percentuale: parseInt(v) })}
          >
            <SelectTrigger className="w-20 h-7 text-sm font-bold">
              <SelectValue>
                <span className="flex items-center gap-1">
                  <Percent className="w-3 h-3" />
                  {colore.percentuale}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PERCENTUALI.map(p => (
                <SelectItem key={p} value={String(p)}>
                  <span className="font-medium">{p}%</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Selezione prodotto */}
      {colore.attivo && (
        <div className="space-y-2">
          <Select 
            value={colore.prodotto_id || undefined} 
            onValueChange={(v) => {
              const prod = prodottiFiltrati.find(p => p.id === v);
              onChange({ 
                ...colore, 
                prodotto_id: v,
                prodotto_nome: prod?.nome || '',
                prodotto_codice: prod?.codice || ''
              });
            }}
          >
            <SelectTrigger className="h-9 text-sm bg-white">
              <SelectValue placeholder="Seleziona colore...">
                {colore.prodotto_nome && (
                  <span className="font-medium">{colore.prodotto_nome}</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {prodottiFiltrati.length > 0 ? (
                prodottiFiltrati.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{p.nome}</span>
                      {p.codice && (
                        <span className="text-xs text-slate-400 ml-2">{p.codice}</span>
                      )}
                    </div>
                  </SelectItem>
                ))
              ) : (
                <div className="p-2 text-sm text-slate-500 text-center">
                  {marcaSelezionata ? "Nessun prodotto per questa marca" : "Seleziona prima una marca"}
                </div>
              )}
            </SelectContent>
          </Select>
          
          {colore.prodotto_nome && (
            <div className="flex items-center gap-1 text-xs text-emerald-600">
              <Check className="w-3 h-3" />
              <span>{colore.prodotto_nome}</span>
              {colore.percentuale > 0 && <span className="font-bold">({colore.percentuale}%)</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPALE - COLOR BAR PRO
// ============================================================
export default function ColorBarPro({ 
  cliente, 
  appuntamento,
  operatori = [],
  open, 
  onOpenChange, 
  onSuccess 
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("formula");
  const [formulaPrecedente, setFormulaPrecedente] = useState(null);

  // Modifica formula dallo storico: riporta in tab formula con riferimento alla vecchia
  const modificaFormulaStorico = (item) => {
    setFormulaPrecedente(item);
    setActiveTab("formula");
    toast.info("Formula precedente caricata come riferimento. Puoi compilarne una nuova o eliminarla.");
  };

  // Elimina formula dallo storico
  const eliminaFormulaStorico = async (item) => {
    if (!window.confirm(`Eliminare definitivamente la formula del ${item.data ? new Date(item.data).toLocaleDateString('it-IT') : ''}?`)) return;
    try {
      await axios.delete(`${API}/colorbar/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Formula eliminata");
      // Pulisci riferimento se era quella mostrata
      if (formulaPrecedente?.id === item.id) setFormulaPrecedente(null);
      // Ricarica storico
      fetchStorico();
    } catch (error) {
      console.error("Errore eliminazione formula:", error?.response?.data);
      toast.error(error?.response?.data?.detail || "Errore eliminazione formula");
    }
  };
  const [prodottiDisponibili, setProdottiDisponibili] = useState([]);
  const [storico, setStorico] = useState([]);
  
  // Sezione BASE (Radici)
  const [baseAttivo, setBaseAttivo] = useState(true);
  const [baseMarca, setBaseMarca] = useState("");
  const [baseColori, setBaseColori] = useState([
    { attivo: true, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 100 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 }
  ]);
  const [baseGrammatura, setBaseGrammatura] = useState("45g");
  const [baseOssigeno, setBaseOssigeno] = useState("20 vol");

  // Sezione TONALIZZANTE (Lunghezze)
  const [tonalizzanteAttivo, setTonalizzanteAttivo] = useState(false);
  const [tonalizzanteMarca, setTonalizzanteMarca] = useState("");
  const [tonalizzanteColori, setTonalizzanteColori] = useState([
    { attivo: true, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 100 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
    { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 }
  ]);
  const [tonalizzanteGrammatura, setTonalizzanteGrammatura] = useState("30g");
  const [tonalizzanteOssigeno, setTonalizzanteOssigeno] = useState("10 vol");

  // Extra data
  const [extraData, setExtraData] = useState({
    operatore_id: "",
    operatore_nome: "",
    tempo_posa: "35",
    note: ""
  });

  // ============================================================
  // DATA FETCHING
  // ============================================================
  const fetchProdotti = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/prodotti/colori-disponibili`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProdottiDisponibili(res.data || []);
    } catch (err) {
      setProdottiDisponibili([]);
    }
  }, [token]);

  // Paginazione storico
  const [storicoSkip, setStoricoSkip] = useState(0);
  const [storicoTotale, setStoricoTotale] = useState(0);
  const [storicoHasMore, setStoricoHasMore] = useState(false);
  const [storicoLoading, setStoricoLoading] = useState(false);
  const STORICO_PAGE_SIZE = 25;

  const fetchStorico = useCallback(async (reset = true) => {
    if (!cliente?.id) return;
    try {
      setStoricoLoading(true);
      const skip = reset ? 0 : storicoSkip;
      const res = await axios.get(
        `${API}/colorbar/cliente/${cliente.id}`,
        {
          params: { paginated: true, limit: STORICO_PAGE_SIZE, skip },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = res.data || {};
      const items = data.items || [];
      if (reset) {
        setStorico(items);
        setStoricoSkip(items.length);
      } else {
        setStorico(prev => [...prev, ...items]);
        setStoricoSkip(prev => prev + items.length);
      }
      setStoricoTotale(data.total || 0);
      setStoricoHasMore(Boolean(data.has_more));
    } catch (err) {
      // ignore
    } finally {
      setStoricoLoading(false);
    }
  }, [cliente?.id, token, storicoSkip]);

  const loadMoreStorico = () => fetchStorico(false);

  useEffect(() => {
    if (open) {
      fetchProdotti();
      fetchStorico();
      if (appuntamento?.operatore_id && operatori.length > 0) {
        const op = operatori.find(o => o.id === appuntamento.operatore_id);
        if (op) {
          setExtraData(prev => ({
            ...prev,
            operatore_id: op.id,
            operatore_nome: `${op.nome} ${op.cognome || ''}`.trim()
          }));
        }
      }
    }
  }, [open, fetchProdotti, fetchStorico, appuntamento, operatori]);

  // ============================================================
  // HELPERS
  // ============================================================
  const getColoriAttivi = (colori) => colori.filter(c => c.attivo && c.prodotto_nome);
  
  const buildFormulaString = (colori, marca, grammatura, ossigeno) => {
    const attivi = getColoriAttivi(colori);
    if (attivi.length === 0) return "";
    
    const coloriStr = attivi.map(c => 
      attivi.length > 1 ? `${c.prodotto_nome} (${c.percentuale}%)` : c.prodotto_nome
    ).join(" + ");
    
    return `${marca}: ${coloriStr} [${grammatura} + ${ossigeno}]`;
  };

  const getTotalePercentuali = (colori) => {
    return getColoriAttivi(colori).reduce((sum, c) => sum + (c.percentuale || 0), 0);
  };

  // ============================================================
  // SAVE
  // ============================================================
  const handleSave = async () => {
    // Validazione
    if (!baseAttivo && !tonalizzanteAttivo) {
      toast.error("Attiva almeno una sezione colore");
      return;
    }
    
    if (baseAttivo && getColoriAttivi(baseColori).length === 0) {
      toast.error("Seleziona almeno un colore per la Base");
      return;
    }

    // Verifica operatore obbligatorio
    if (!extraData.operatore_id) {
      toast.error("Seleziona un operatore prima di salvare");
      return;
    }

    const baseTotale = getTotalePercentuali(baseColori);
    if (baseAttivo && baseTotale !== 100 && getColoriAttivi(baseColori).length > 1) {
      toast.warning(`Le percentuali Base devono sommare 100% (attuale: ${baseTotale}%)`);
    }

    setLoading(true);
    try {
      // Validazione operatore: OBBLIGATORIO per il backend
      if (!extraData.operatore_id) {
        toast.error("Seleziona un Operatore prima di salvare la formula");
        setLoading(false);
        return;
      }

      const baseFormula = buildFormulaString(baseColori, baseMarca, baseGrammatura, baseOssigeno);
      const tonalizzanteFormula = buildFormulaString(tonalizzanteColori, tonalizzanteMarca, tonalizzanteGrammatura, tonalizzanteOssigeno);

      // Validazione: almeno una formula deve essere stata compilata
      if (!baseFormula && !tonalizzanteFormula) {
        toast.error("Compila almeno una sezione (Base o Tonalizzante) prima di salvare");
        setLoading(false);
        return;
      }

      const payload = {
        cliente_id: cliente.id,
        cliente_nome: `${cliente.nome} ${cliente.cognome || ''}`.trim(),
        appuntamento_id: appuntamento?.id || null,
        operatore_id: extraData.operatore_id || "",
        operatore_nome: extraData.operatore_nome || "",
        tipo_colore: baseMarca || tonalizzanteMarca || "Colore",
        formula_colore: [baseFormula, tonalizzanteFormula].filter(Boolean).join(' | '),
        ossigeno_volume: baseOssigeno || tonalizzanteOssigeno || "",
        tempo_posa: extraData.tempo_posa || "",
        note_tecniche: extraData.note || "",
        confermato: true,
        // Struttura dettagliata
        formula_dettaglio: {
          base: baseAttivo ? {
            marca: baseMarca,
            colori: getColoriAttivi(baseColori),
            grammatura: baseGrammatura,
            ossigeno: baseOssigeno
          } : null,
          tonalizzante: tonalizzanteAttivo ? {
            marca: tonalizzanteMarca,
            colori: getColoriAttivi(tonalizzanteColori),
            grammatura: tonalizzanteGrammatura,
            ossigeno: tonalizzanteOssigeno
          } : null
        }
      };

      await axios.post(
        `${API}/colorbar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Scheda colore salvata! La vecchia formula rimane nello storico.");
      // Pulisci form e riferimento "formula precedente"
      setFormulaPrecedente(null);
      handleReset();
      // Ricarica storico e mostra tab Storico per visualizzare vecchia + nuova
      try { await fetchStorico(); } catch (e) { /* ignore */ }
      setActiveTab("storico");
      onSuccess?.();
    } catch (error) {
      console.error("Errore POST /colorbar:", error?.response?.status, error?.response?.data);
      const msg = error?.response?.data?.detail || error?.message || "Errore nel salvataggio";
      toast.error(typeof msg === 'string' ? msg : "Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBaseMarca("");
    setBaseColori([
      { attivo: true, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 100 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 }
    ]);
    setBaseGrammatura("45g");
    setBaseOssigeno("20 vol");
    setTonalizzanteMarca("");
    setTonalizzanteColori([
      { attivo: true, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 100 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 },
      { attivo: false, prodotto_id: "", prodotto_nome: "", prodotto_codice: "", percentuale: 0 }
    ]);
  };

  const updateColore = (section, index, newColore) => {
    if (section === 'base') {
      const newColori = [...baseColori];
      newColori[index] = newColore;
      setBaseColori(newColori);
    } else {
      const newColori = [...tonalizzanteColori];
      newColori[index] = newColore;
      setTonalizzanteColori(newColori);
    }
  };

  // ============================================================
  // RENDER SEZIONE COLORE
  // ============================================================
  const renderSezioneColore = (
    titolo, 
    icona, 
    gradiente,
    attivo, 
    setAttivo,
    marca,
    setMarca,
    colori,
    setColori,
    grammatura,
    setGrammatura,
    ossigeno,
    setOssigeno,
    grammature,
    section
  ) => {
    const totalePercentuali = getTotalePercentuali(colori);
    const coloriAttivi = getColoriAttivi(colori);

    return (
      <div className={`rounded-xl sm:rounded-2xl border-2 transition-all ${
        attivo 
          ? `border-transparent shadow-lg ${gradiente}` 
          : 'border-dashed border-slate-300 bg-slate-50'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-3 sm:p-4 ${
          attivo ? 'bg-white/90 backdrop-blur-sm rounded-t-xl' : ''
        }`}>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`p-1.5 sm:p-2 rounded-lg ${attivo ? gradiente : 'bg-slate-200'}`}>
              {icona}
            </div>
            <div>
              <h3 className={`font-bold text-base sm:text-lg ${attivo ? 'text-slate-800' : 'text-slate-400'}`}>
                {titolo}
              </h3>
              <p className="text-[10px] sm:text-xs text-slate-500">
                {section === 'base' ? "Ritocco radici" : "Lunghezze e punte"}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setAttivo(!attivo)}
            className={`relative w-12 sm:w-14 h-6 sm:h-7 rounded-full transition-all ${
              attivo ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 'bg-slate-300'
            }`}
          >
            <span className={`absolute top-0.5 sm:top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${
              attivo ? 'left-6 sm:left-8' : 'left-0.5 sm:left-1'
            }`}>
              {attivo && <Check className="w-3 h-3 text-emerald-500 m-1" />}
            </span>
          </button>
        </div>

        {/* Contenuto */}
        {attivo && (
          <div className="p-3 sm:p-4 pt-0 space-y-4 bg-white/80">
            {/* Selezione Marca */}
            <div>
              <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1.5">
                <Package className="w-3 h-3" /> MARCA / LINEA
              </Label>
              <Select value={marca || undefined} onValueChange={setMarca}>
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Seleziona marca..." />
                </SelectTrigger>
                <SelectContent>
                  {prodottiDisponibili.map(m => (
                    <SelectItem key={m.marca} value={m.marca}>
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium">{m.marca}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {m.num_prodotti} colori
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selezione Colori (1-4) */}
            {marca && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-slate-600 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <Beaker className="w-3 h-3" /> COLORI (max 4)
                  </span>
                  {coloriAttivi.length > 1 && (
                    <span className={`text-xs ${totalePercentuali === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      Totale: {totalePercentuali}%
                    </span>
                  )}
                </Label>
                
                <div className="grid grid-cols-2 gap-2">
                  {colori.map((colore, idx) => (
                    <ColorSelector
                      key={`${section}-${idx}-${colore.colore || 'empty'}`}
                      index={idx}
                      colore={colore}
                      onChange={(newColore) => updateColore(section, idx, newColore)}
                      prodottiDisponibili={prodottiDisponibili}
                      marcaSelezionata={marca}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Grammatura e Ossigeno */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1.5">
                  <Scale className="w-3 h-3" /> GRAMMATURA
                </Label>
                <Select value={grammatura} onValueChange={setGrammatura}>
                  <SelectTrigger className="h-9 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {grammature.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1.5">
                  <FlaskConical className="w-3 h-3" /> OSSIGENO
                </Label>
                <Select value={ossigeno} onValueChange={setOssigeno}>
                  <SelectTrigger className="h-9 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OSSIGENI.map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview Formula */}
            {coloriAttivi.length > 0 && (
              <div className="p-3 bg-slate-800 rounded-xl text-white">
                <p className="text-[10px] text-slate-400 mb-1">FORMULA</p>
                <p className="font-mono text-sm break-all">
                  {buildFormulaString(colori, marca, grammatura, ossigeno)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER MAIN
  // ============================================================
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 bg-gradient-to-br from-slate-50 to-white overflow-hidden">
        {/* Header */}
        <div className="relative overflow-hidden shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 opacity-90" />
          <div className="relative p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                  <Palette className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">Color Bar Pro</h2>
                  <p className="text-xs sm:text-sm text-white/80 truncate max-w-[180px] sm:max-w-none">
                    {cliente?.nome} {cliente?.cognome}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-3 sm:px-4 pt-2 shrink-0">
            <TabsList className="w-full grid grid-cols-3 h-10 bg-slate-100/80">
              <TabsTrigger value="formula" className="gap-1.5 text-xs data-[state=active]:bg-white">
                <Droplets className="w-3.5 h-3.5" /> Formula
              </TabsTrigger>
              <TabsTrigger value="storico" className="gap-1.5 text-xs data-[state=active]:bg-white">
                <History className="w-3.5 h-3.5" /> Storico
                {storicoTotale > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {storicoTotale}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-1.5 text-xs data-[state=active]:bg-white">
                <Settings className="w-3.5 h-3.5" /> Info
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab Formula */}
          <TabsContent value="formula" className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 mt-3 space-y-4">
            {/* Banner Formula PRECEDENTE - riferimento per la modifica */}
            {formulaPrecedente && (
              <div className="p-3 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-300 rounded-xl" data-testid="formula-precedente-banner">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">📌 Formula precedente (riferimento)</p>
                    <p className="font-semibold text-amber-900 text-sm mt-1 break-words">{formulaPrecedente.formula_colore || 'Formula'}</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {formulaPrecedente.data ? new Date(formulaPrecedente.data).toLocaleDateString('it-IT') : '-'}
                      {formulaPrecedente.operatore_nome && ` • ${formulaPrecedente.operatore_nome}`}
                      {formulaPrecedente.tempo_posa && ` • posa ${formulaPrecedente.tempo_posa}'`}
                    </p>
                    {formulaPrecedente.note_tecniche && (
                      <p className="text-xs text-amber-600 mt-1 italic">📝 {formulaPrecedente.note_tecniche}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => eliminaFormulaStorico(formulaPrecedente)}
                      className="p-1.5 rounded-md bg-red-100 hover:bg-red-200 text-red-700 transition"
                      title="Elimina formula precedente"
                      data-testid="btn-elimina-formula-precedente"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormulaPrecedente(null)}
                      className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition"
                      title="Chiudi riferimento"
                      data-testid="btn-chiudi-formula-precedente"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Sezione BASE */}
            {renderSezioneColore(
              "Colore Base",
              <Droplets className="w-4 h-4 sm:w-5 sm:h-5 text-white" />,
              "bg-gradient-to-br from-amber-400 to-orange-500",
              baseAttivo,
              setBaseAttivo,
              baseMarca,
              setBaseMarca,
              baseColori,
              setBaseColori,
              baseGrammatura,
              setBaseGrammatura,
              baseOssigeno,
              setBaseOssigeno,
              GRAMMATURE_BASE,
              'base'
            )}

            {/* Sezione TONALIZZANTE */}
            {renderSezioneColore(
              "Tonalizzante",
              <Waves className="w-4 h-4 sm:w-5 sm:h-5 text-white" />,
              "bg-gradient-to-br from-violet-400 to-purple-500",
              tonalizzanteAttivo,
              setTonalizzanteAttivo,
              tonalizzanteMarca,
              setTonalizzanteMarca,
              tonalizzanteColori,
              setTonalizzanteColori,
              tonalizzanteGrammatura,
              setTonalizzanteGrammatura,
              tonalizzanteOssigeno,
              setTonalizzanteOssigeno,
              GRAMMATURE_TONALIZZANTE,
              'tonalizzante'
            )}

            {/* Dettagli Extra */}
            <div className="p-3 bg-slate-50 rounded-xl space-y-3">
              <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Dettagli
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">
                    Operatore <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={extraData.operatore_id || undefined} 
                    onValueChange={(v) => {
                      const op = operatori.find(o => o.id === v);
                      setExtraData(prev => ({
                        ...prev,
                        operatore_id: v,
                        operatore_nome: op ? `${op.nome} ${op.cognome || ''}`.trim() : ''
                      }));
                    }}
                  >
                    <SelectTrigger className={`h-9 bg-white ${!extraData.operatore_id ? 'border-red-300 border-2' : 'border-green-300'}`}>
                      <SelectValue placeholder="⚠️ Seleziona operatore..." />
                    </SelectTrigger>
                    <SelectContent>
                      {operatori.map(op => (
                        <SelectItem key={op.id} value={op.id}>
                          {op.nome} {op.cognome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Tempo Posa (min)</Label>
                  <Input 
                    type="number"
                    value={extraData.tempo_posa}
                    onChange={(e) => setExtraData(prev => ({ ...prev, tempo_posa: e.target.value }))}
                    className="h-9 bg-white"
                    placeholder="35"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Note</Label>
                <Textarea 
                  value={extraData.note}
                  onChange={(e) => setExtraData(prev => ({ ...prev, note: e.target.value }))}
                  className="bg-white resize-none h-14 text-sm"
                  placeholder="Note tecniche..."
                />
              </div>
            </div>

            {/* Azioni */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={handleReset} className="gap-1.5 h-10">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="flex-1 h-10 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Salvataggio..." : "Salva"}
              </Button>
            </div>
          </TabsContent>

          {/* Tab Storico */}
          <TabsContent value="storico" className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 mt-3">
            {storico.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Nessuno storico per questo cliente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {storico.map((item, idx) => (
                  <div 
                    key={item.id || idx}
                    className="p-3 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm">{item.formula_colore || 'Formula'}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {item.data ? new Date(item.data).toLocaleDateString('it-IT') : '-'}
                          {item.operatore_nome && ` • ${item.operatore_nome}`}
                          {item.tempo_posa && ` • posa ${item.tempo_posa}'`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => modificaFormulaStorico(item)}
                          data-testid={`btn-modifica-formula-${idx}`}
                          title="Modifica formula"
                        >
                          <Edit2 className="w-3 h-3 mr-1" /> Modifica
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-xs text-red-600 hover:bg-red-50 border-red-200"
                          onClick={() => eliminaFormulaStorico(item)}
                          data-testid={`btn-elimina-formula-${idx}`}
                          title="Elimina formula"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Pulsante "Carica altri" per paginazione storico */}
                {storicoHasMore && (
                  <Button
                    variant="outline"
                    onClick={loadMoreStorico}
                    disabled={storicoLoading}
                    className="w-full mt-2 text-xs"
                    data-testid="colorbar-storico-load-more"
                  >
                    {storicoLoading
                      ? "Caricamento..."
                      : `Carica altri ${Math.min(STORICO_PAGE_SIZE, storicoTotale - storico.length)} di ${storicoTotale - storico.length} rimanenti`}
                  </Button>
                )}
                {!storicoHasMore && storico.length >= 25 && (
                  <p className="text-center text-xs text-slate-400 mt-2">
                    Hai visto tutti i {storicoTotale} trattamenti
                  </p>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab Info */}
          <TabsContent value="settings" className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 mt-3">
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Prodotti dal Magazzino
                </h4>
                <p className="text-sm text-amber-700 mt-2">
                  I colori disponibili vengono caricati direttamente dal Magazzino. 
                  Aggiungi prodotti colore nel Magazzino per vederli qui.
                </p>
              </div>

              <div className="p-4 bg-white border border-slate-200 rounded-xl">
                <h4 className="font-semibold text-slate-800 mb-3">Marche Disponibili</h4>
                <div className="flex flex-wrap gap-2">
                  {prodottiDisponibili.map(m => (
                    <Badge key={m.marca} variant="secondary" className="px-3 py-1">
                      {m.marca} ({m.num_prodotti})
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
